import React, { useState, useLayoutEffect } from 'react'
import { useCachedImage } from '@renderer/hooks/useCachedImage'

interface PhotoFrameProps {
  src: string
  alt?: string
  displayRatio?: number // The ratio to display in (e.g. 1/1)
  className?: string
  isMirrored?: boolean
}

type LoadState = 'loading' | 'ready' | 'error'

const PhotoFrame: React.FC<PhotoFrameProps> = ({
  src,
  alt = '',
  displayRatio = 1,
  className = '',
  isMirrored = false
}) => {
  const [sourceRatio, setSourceRatio] = useState<number | null>(null)
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const cachedSrc = useCachedImage(src)

  // Reset when the URL changes so a failed prior src can't leave us stuck blank.
  // A layout effect, because an image already decoded can fire onLoad before a
  // passive effect runs, and resetting after that hides it with no onLoad to come.
  useLayoutEffect(() => {
    setSourceRatio(null)
    setLoadState('loading')
  }, [cachedSrc])

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    aspectRatio: `${displayRatio}`,
    overflow: 'hidden',
    backgroundColor: loadState === 'ready' ? undefined : 'rgba(0, 0, 0, 0.06)'
  }

  // Fill the display ratio and crop excess once we know the source aspect.
  const imageStyle: React.CSSProperties = sourceRatio
    ? {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%) ${isMirrored ? 'scaleX(-1)' : ''}`,
        transformOrigin: 'center',
        width: sourceRatio > displayRatio ? `${(sourceRatio / displayRatio) * 100}%` : '100%',
        height: sourceRatio > displayRatio ? '100%' : `${(displayRatio / sourceRatio) * 100}%`,
        objectFit: 'cover'
      }
    : {
        // Keep the img in-tree so onLoad/onError can fire, but don't flash uncropped content.
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        opacity: 0
      }

  // Blob/data URLs don't need CORS; anonymous on http can fail opaque CDN responses.
  const crossOrigin =
    cachedSrc.startsWith('http://') || cachedSrc.startsWith('https://') ? 'anonymous' : undefined

  return (
    <div style={containerStyle} className={className}>
      {loadState === 'loading' && (
        <div aria-hidden className="absolute inset-0 animate-pulse bg-black/10" />
      )}
      {loadState === 'error' && (
        <div
          role="img"
          aria-label={alt || 'Photo failed to load'}
          className="absolute inset-0 flex select-none items-center justify-center text-xs text-black/35"
        >
          —
        </div>
      )}
      {loadState !== 'error' && (
        <img
          key={cachedSrc}
          src={cachedSrc}
          alt={alt}
          style={imageStyle}
          crossOrigin={crossOrigin}
          onLoad={(e): void => {
            const img = e.currentTarget
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              setSourceRatio(img.naturalWidth / img.naturalHeight)
              setLoadState('ready')
            } else {
              setLoadState('error')
            }
          }}
          onError={(): void => {
            setSourceRatio(null)
            setLoadState('error')
          }}
        />
      )}
    </div>
  )
}

export default PhotoFrame
